<div>
  <div class="container-fluid">
    <!-- Back Button -->
    <div class="mb-3">
      <button type="button" class="btn btn-outline-secondary" (click)="cancel()">
        <i class="bi bi-arrow-left me-2"></i>
        {{ isEditMode ? 'Back to Organization' : 'Back to Organizations' }}
      </button>
    </div>

    <!-- Page Header -->
    <div class="mb-4">
      <h1 class="h2 mb-1 fw-bold">{{ pageTitle }}</h1>
      <p class="text-muted mb-0">
        {{ isEditMode ? 'Update organization details' : 'Add a new organization to the system' }}
      </p>
    </div>

    <!-- Loading State -->
    <div *ngIf="loadingOrganization" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Loading organization...</p>
    </div>

    <!-- Error Message -->
    <div *ngIf="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''" aria-label="Close"></button>
    </div>

    <!-- Success Message -->
    <div *ngIf="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
      <i class="bi bi-check-circle-fill me-2"></i>
      {{ successMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="successMessage = ''" aria-label="Close"></button>
    </div>

    <!-- Form -->
    <form *ngIf="!loadingOrganization" [formGroup]="organizationForm" (ngSubmit)="onSubmit()">
      <div class="row">
        <!-- Left Column - Main Info -->
        <div class="col-lg-8 mb-4">
          <!-- Basic Information Card -->
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">
                <i class="bi bi-building me-2"></i>
                Basic Information
              </h5>
            </div>
            <div class="card-body">
              <div class="row">
                <!-- Organization Name -->
                <div class="col-md-8 mb-3">
                  <label for="name" class="form-label">Organization Name <span class="text-danger">*</span></label>
                  <input
                    type="text"
                    class="form-control"
                    id="name"
                    formControlName="name"
                    placeholder="Enter organization name"
                    [class.is-invalid]="organizationForm.get('name')?.invalid && organizationForm.get('name')?.touched"
                  />
                  <div *ngIf="organizationForm.get('name')?.invalid && organizationForm.get('name')?.touched" class="invalid-feedback">
                    Organization name is required (minimum 2 characters)
                  </div>
                </div>

                <!-- Organization Type -->
                <div class="col-md-4 mb-3">
                  <label for="organizationType" class="form-label">Organization Type <span class="text-danger">*</span></label>
                  <select
                    class="form-select"
                    id="organizationType"
                    formControlName="organizationType"
                    [class.is-invalid]="organizationForm.get('organizationType')?.invalid && organizationForm.get('organizationType')?.touched"
                  >
                    <option *ngFor="let type of organizationTypes" [value]="type.value">
                      {{ type.label }}
                    </option>
                  </select>
                </div>

                <!-- EIN (for business entities) -->
                <div class="col-md-6 mb-3" *ngIf="showEinField">
                  <label for="ein" class="form-label">
                    EIN (Employer Identification Number)
                    <span class="text-danger" *ngIf="einRequired">*</span>
                  </label>
                  <input
                    type="text"
                    class="form-control"
                    id="ein"
                    formControlName="ein"
                    placeholder="XX-XXXXXXX"
                    [class.is-invalid]="organizationForm.get('ein')?.invalid && organizationForm.get('ein')?.touched"
                  />
                  <div *ngIf="organizationForm.get('ein')?.invalid && organizationForm.get('ein')?.touched" class="invalid-feedback">
                    <span *ngIf="organizationForm.get('ein')?.errors?.['required']">EIN is required for this organization type</span>
                    <span *ngIf="organizationForm.get('ein')?.errors?.['pattern']">Please enter a valid EIN (XX-XXXXXXX)</span>
                  </div>
                  <div class="form-text">9-digit number assigned by the IRS</div>
                </div>

                <!-- SSN (for individuals) -->
                <div class="col-md-6 mb-3" *ngIf="showSsnField">
                  <label for="ssn" class="form-label">
                    SSN (Social Security Number)
                    <span class="text-danger" *ngIf="ssnRequired">*</span>
                  </label>
                  <input
                    type="text"
                    class="form-control"
                    id="ssn"
                    formControlName="ssn"
                    placeholder="XXX-XX-XXXX"
                    [class.is-invalid]="organizationForm.get('ssn')?.invalid && organizationForm.get('ssn')?.touched"
                  />
                  <div *ngIf="organizationForm.get('ssn')?.invalid && organizationForm.get('ssn')?.touched" class="invalid-feedback">
                    <span *ngIf="organizationForm.get('ssn')?.errors?.['required']">SSN is required for this organization type</span>
                    <span *ngIf="organizationForm.get('ssn')?.errors?.['pattern']">Please enter a valid SSN (XXX-XX-XXXX)</span>
                  </div>
                  <div class="form-text">9-digit number for individual tax identification</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Address Card -->
          <div class="card mt-4">
            <div class="card-header">
              <h5 class="mb-0">
                <i class="bi bi-geo-alt me-2"></i>
                Address
              </h5>
            </div>
            <div class="card-body">
              <div class="row">
                <!-- Street Address Line 1 -->
                <div class="col-12 mb-3">
                  <label for="streetAddressLine1" class="form-label">Street Address <span class="text-danger">*</span></label>
                  <input
                    type="text"
                    class="form-control"
                    id="streetAddressLine1"
                    formControlName="streetAddressLine1"
                    placeholder="123 Main Street"
                    [class.is-invalid]="organizationForm.get('streetAddressLine1')?.invalid && organizationForm.get('streetAddressLine1')?.touched"
                  />
                  <div *ngIf="organizationForm.get('streetAddressLine1')?.invalid && organizationForm.get('streetAddressLine1')?.touched" class="invalid-feedback">
                    Street address is required
                  </div>
                </div>

                <!-- Street Address Line 2 -->
                <div class="col-12 mb-3">
                  <label for="streetAddressLine2" class="form-label">Street Address Line 2</label>
                  <input
                    type="text"
                    class="form-control"
                    id="streetAddressLine2"
                    formControlName="streetAddressLine2"
                    placeholder="Suite, Unit, Building (optional)"
                  />
                </div>

                <!-- City -->
                <div class="col-md-5 mb-3">
                  <label for="city" class="form-label">City <span class="text-danger">*</span></label>
                  <input
                    type="text"
                    class="form-control"
                    id="city"
                    formControlName="city"
                    placeholder="City"
                    [class.is-invalid]="organizationForm.get('city')?.invalid && organizationForm.get('city')?.touched"
                  />
                  <div *ngIf="organizationForm.get('city')?.invalid && organizationForm.get('city')?.touched" class="invalid-feedback">
                    City is required
                  </div>
                </div>

                <!-- State -->
                <div class="col-md-4 mb-3">
                  <label for="state" class="form-label">State <span class="text-danger">*</span></label>
                  <app-us-state-select
                    inputId="state"
                    formControlName="state"
                    [invalid]="organizationForm.get('state')?.invalid && organizationForm.get('state')?.touched"
                  ></app-us-state-select>
                  <div *ngIf="organizationForm.get('state')?.invalid && organizationForm.get('state')?.touched" class="invalid-feedback">
                    State is required
                  </div>
                </div>

                <!-- ZIP Code -->
                <div class="col-md-3 mb-3">
                  <label for="zipCode" class="form-label">ZIP Code <span class="text-danger">*</span></label>
                  <input
                    type="text"
                    class="form-control"
                    id="zipCode"
                    formControlName="zipCode"
                    placeholder="12345"
                    [class.is-invalid]="organizationForm.get('zipCode')?.invalid && organizationForm.get('zipCode')?.touched"
                  />
                  <div *ngIf="organizationForm.get('zipCode')?.invalid && organizationForm.get('zipCode')?.touched" class="invalid-feedback">
                    Valid ZIP code is required (e.g., 12345 or 12345-6789)
                  </div>
                </div>

                <!-- Country -->
                <div class="col-md-4 mb-3">
                  <label for="country" class="form-label">Country <span class="text-danger">*</span></label>
                  <input
                    type="text"
                    class="form-control"
                    id="country"
                    formControlName="country"
                    [class.is-invalid]="organizationForm.get('country')?.invalid && organizationForm.get('country')?.touched"
                  />
                  <div *ngIf="organizationForm.get('country')?.invalid && organizationForm.get('country')?.touched" class="invalid-feedback">
                    Country is required
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Contact Information Card -->
          <div class="card mt-4">
            <div class="card-header">
              <h5 class="mb-0">
                <i class="bi bi-telephone me-2"></i>
                Contact Information
              </h5>
            </div>
            <div class="card-body">
              <div class="row">
                <!-- Email -->
                <div class="col-md-6 mb-3">
                  <label for="email" class="form-label">Email Address</label>
                  <input
                    type="email"
                    class="form-control"
                    id="email"
                    formControlName="email"
                    placeholder="contact@organization.com"
                    [class.is-invalid]="organizationForm.get('email')?.invalid && organizationForm.get('email')?.touched"
                  />
                  <div *ngIf="organizationForm.get('email')?.invalid && organizationForm.get('email')?.touched" class="invalid-feedback">
                    Please enter a valid email address
                  </div>
                </div>

                <!-- Phone -->
                <div class="col-md-6 mb-3">
                  <label for="phone" class="form-label">Phone Number</label>
                  <input
                    type="tel"
                    class="form-control"
                    id="phone"
                    formControlName="phone"
                    mask="(000) 000-0000"
                    [validation]="false"
                    placeholder="(555) 123-4567"
                    [class.is-invalid]="organizationForm.get('phone')?.invalid && organizationForm.get('phone')?.touched"
                  />
                  <div *ngIf="organizationForm.get('phone')?.invalid && organizationForm.get('phone')?.touched" class="invalid-feedback">
                    Please enter a valid phone number
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column - Sidebar -->
        <div class="col-lg-4">
          <!-- Billing Settings Card -->
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0">
                <i class="bi bi-credit-card me-2"></i>
                Billing Settings
              </h5>
            </div>
            <div class="card-body">
              <label class="form-label">Billing Responsibility <span class="text-danger">*</span></label>
              <div class="vstack gap-2">
                <div
                  *ngFor="let option of billingOptions"
                  class="billing-option d-flex align-items-start gap-2 p-3 border rounded-3 cursor-pointer"
                  [ngClass]="{ 'border-primary bg-primary bg-opacity-10': organizationForm.get('billingResponsibility')?.value === option.value }"
                  (click)="organizationForm.get('billingResponsibility')?.setValue(option.value)"
                >
                  <div>
                    <input
                      type="radio"
                      class="form-check-input fs-5 mt-0 cursor-pointer"
                      [id]="'billing-' + option.value"
                      [value]="option.value"
                      formControlName="billingResponsibility"
                    />
                  </div>
                  <label [for]="'billing-' + option.value" class="small mb-0 cursor-pointer">
                    {{ option.label }}
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Form Actions Card -->
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">
                <i class="bi bi-check2-square me-2"></i>
                Actions
              </h5>
            </div>
            <div class="card-body">
              <div class="d-grid gap-2">
                <button
                  type="submit"
                  class="btn btn-primary"
                  [disabled]="loading || organizationForm.invalid"
                >
                  <span *ngIf="loading" class="spinner-border spinner-border-sm me-2"></span>
                  <i *ngIf="!loading" class="bi me-2" [ngClass]="isEditMode ? 'bi-save' : 'bi-plus-lg'"></i>
                  {{ submitButtonText }}
                </button>
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  (click)="cancel()"
                  [disabled]="loading"
                >
                  <i class="bi bi-x-lg me-2"></i>
                  Cancel
                </button>
              </div>
            </div>
          </div>

          <!-- Help Card -->
          <div class="card mt-4">
            <div class="card-body">
              <div class="d-flex align-items-start gap-3">
                <i class="bi bi-info-circle fs-4 text-primary flex-shrink-0"></i>
                <div>
                  <h6 class="small fw-semibold mb-1">Need Help?</h6>
                  <p class="text-muted mb-0 small">
                    Organizations are the primary billing and management entities in the system.
                    Members can be invited to join organizations after creation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</div>
